// Meta Pixel for the web app. No-ops when REACT_APP_META_PIXEL_ID is unset.
// ONE install for the whole SPA (mounted by WebAnalytics). Purchase is fired
// with eventID `purchase_<order_id>` — the backend sends the same event via the
// Conversions API, Meta de-duplicates the pair.
import { hasSensitiveParams, sanitizeUrl } from './attribution';

const pixelId = (process.env.REACT_APP_META_PIXEL_ID || '').trim();
let ready = false;
let lastPageViewKey = '';

export function initMetaPixel() {
  if (!pixelId || typeof window === 'undefined') return false;
  if (ready) return true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
  ready = true;
  return true;
}

export const metaEnabled = () => ready && !!window.fbq;

/** One PageView per real route. Skips the bare `/` bounce (it redirects before
 *  the visitor sees anything) and never fires while a token/OTP is in the URL. */
export function metaPageView(pathWithSearch) {
  if (!metaEnabled()) return false;
  const path = pathWithSearch || `${window.location.pathname}${window.location.search}`;
  const pathname = path.split('?')[0];
  if (pathname === '/' || hasSensitiveParams(path)) return false;
  if (lastPageViewKey === path) return false;   // same route re-rendered — not a new view
  lastPageViewKey = path;
  window.fbq('track', 'PageView');
  return true;
}

export function metaTrack(name, data = {}, eventID) {
  if (!metaEnabled() || !name) return;
  if (eventID) window.fbq('track', name, data, { eventID });
  else window.fbq('track', name, data);
}

export function metaCustom(name, data = {}) {
  if (!metaEnabled() || !name) return;
  window.fbq('trackCustom', name, data);
}

const cookie = (name) => (document.cookie.split('; ').find((c) => c.startsWith(`${name}=`)) || '').split('=')[1] || '';

/** fbc from the cookie, or rebuilt from a stored fbclid when the pixel hasn't set it yet. */
export const metaFbc = (fbclid) => {
  const c = cookie('_fbc');
  if (c) return c;
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : '';
};

/** Attribution captured at checkout so the server-side Purchase can be matched to this browser. */
export const metaTracking = () => ({ fbp: cookie('_fbp'), fbc: cookie('_fbc'), source_url: sanitizeUrl(window.location.href) });

export const purchaseEventId = (orderId) => `purchase_${orderId}`;

export const PRODUCT_NAME = { call_15: '15-min 1:1 call', call_30: '30-min 1:1 call', call_60: '60-min 1:1 call', chat: 'Paid chat (24h)', quick_question: 'Quick question' };

const productData = ({ product, value, currency = 'INR', mentorId }) => ({
  value: Number(value) || 0, currency, content_ids: [product], content_name: PRODUCT_NAME[product] || product,
  content_type: 'product', content_category: 'mentor_booking', num_items: 1, ...(mentorId ? { mentor_id: mentorId, session_type: product } : {}),
});

/** Mentor profile view — the retargeting signal (content_type `mentor`). */
export function metaViewMentor({ mentorId, mentorName, fromPrice, currency = 'INR' }) {
  metaTrack('ViewContent', {
    content_ids: [mentorId], content_name: mentorName, content_type: 'mentor', content_category: 'mentor_profile',
    ...(fromPrice > 0 ? { value: Number(fromPrice), currency } : {}),
  });
}

export function metaInitiateCheckout({ product, value, currency = 'INR', mentorId, coupon }) {
  metaTrack('InitiateCheckout', { ...productData({ product, value, currency, mentorId }), ...(coupon ? { coupon: coupon } : {}) });
}

/** Call ONLY after the backend reports the order as paid. Value = amount actually paid. */
export function metaPurchase(order) {
  if (!order?.id || order.payment_status !== 'paid') return;
  const value = Number(order.net_amount) || 0;
  metaTrack('Purchase', {
    ...productData({ product: order.product, value, currency: order.currency || 'INR', mentorId: order.mentor_id }),
    contents: [{ id: order.product, quantity: 1, item_price: value }],
    order_id: order.id, ...(order.coupon_code ? { coupon: order.coupon_code } : {}),
  }, purchaseEventId(order.id));
}
